import React from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import {CreditCard, ShieldCheck, Download, CheckCircle, ArrowRight, Lock, Zap, RefreshCw} from 'lucide-react'

export default function StripePaymentPage() {
  const features = [
    {
      icon: Lock,
      title: 'SCA-Ready Compliance',
      description: 'Fully compliant with Strong Customer Authentication (SCA) and PSD2 regulations for European payments.'
    },
    {
      icon: Zap,
      title: 'Instant Processing',
      description: 'Fast, reliable payment processing with automatic retry logic for failed transactions.'
    },
    {
      icon: RefreshCw,
      title: 'Recurring Billing',
      description: 'Seamless subscription management with automatic renewal and dunning management.'
    },
    {
      icon: CreditCard,
      title: 'Multiple Payment Methods',
      description: 'Support for credit cards, debit cards, Apple Pay, Google Pay, and more.'
    }
  ]

  const benefits = [
    'Accept payments from 135+ currencies',
    'Reduce payment fraud with Stripe Radar',
    'Automatic invoice generation',
    'Real-time payment notifications',
    '3D Secure authentication built-in',
    'PCI-DSS Level 1 compliant',
    'Detailed transaction reports',
    'Webhook support for automation'
  ]

  return (
    <div className="min-h-screen bg-white">
      <Navigation />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 to-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                <span className="text-sm font-semibold text-green-700">Official Licensed Integration</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-6">
                WHMCS Stripe Payment Gateway
              </h1>
              
              <p className="text-xl text-slate-600 mb-8">
                Secure, SCA-ready credit card processing. Seamless integration for recurring billing 
                with the world's most trusted payment platform.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
                  Buy Now - $129
                  <ArrowRight className="ml-2 w-5 h-5" />
                </button>
                <a
                  href="#features"
                  className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg border-2 border-blue-600 hover:bg-blue-50 transition-all"
                >
                  View Features
                </a>
              </div>

              <div className="flex items-center space-x-2 text-sm text-slate-600">
                <Download className="w-4 h-4" />
                <span>Instant digital download • Free lifetime updates</span>
              </div>
            </div>

            <div className="bg-slate-100 rounded-xl p-8 border-2 border-slate-200">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="flex items-center justify-between mb-4">
                  <p className="font-semibold text-slate-900">Payment Processing</p>
                  <div className="flex space-x-2">
                    <div className="w-8 h-6 bg-blue-600 rounded"></div>
                    <div className="w-8 h-6 bg-orange-500 rounded"></div>
                    <div className="w-8 h-6 bg-red-600 rounded"></div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                    <span className="text-sm text-slate-700">Invoice #1234</span>
                    <span className="text-sm font-semibold text-green-700">$99.00 ✓</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-green-50 rounded border border-green-200">
                    <span className="text-sm text-slate-700">Invoice #1235</span>
                    <span className="text-sm font-semibold text-green-700">$149.00 ✓</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded border border-blue-200">
                    <span className="text-sm text-slate-700">Invoice #1236</span>
                    <span className="text-sm font-semibold text-blue-700">Processing...</span>
                  </div>
                </div>
                <p className="text-xs text-slate-500 mt-4 text-center">Secure payment processing via Stripe</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Enterprise-Grade Payment Processing
            </h2>
            <p className="text-lg text-slate-600">
              Everything you need for secure, compliant payment processing
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-slate-50 p-6 rounded-xl border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                <p className="text-slate-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Why Choose Our Stripe Integration?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                <span className="text-slate-700">{benefit}</span>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-xl p-8 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">
              Start Accepting Payments in Minutes
            </h3>
            <p className="text-blue-100 mb-6">
              Trusted by thousands of hosting providers worldwide
            </p>
            <button className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-semibold rounded-lg hover:bg-blue-50 transition-all hover:shadow-xl">
              Purchase Now - $129
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Security Notice */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="bg-green-50 border-l-4 border-green-400 p-6 rounded-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <ShieldCheck className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-semibold text-green-900 mb-2">
                  Bank-Level Security Standards
                </h3>
                <p className="text-green-800">
                  This integration uses Stripe's industry-leading security infrastructure. 
                  All payment data is encrypted and handled entirely by Stripe—no sensitive card information 
                  ever touches your server. PCI-DSS Level 1 certified.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white border-2 border-blue-600 rounded-2xl p-8 shadow-xl">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold text-blue-600 mb-2">ONE-TIME PAYMENT</p>
              <p className="text-5xl font-bold text-slate-900 mb-2">$129</p>
              <p className="text-slate-600">Lifetime license • Free updates</p>
            </div>

            <div className="space-y-3 mb-8">
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Full Stripe API integration</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">SCA & 3D Secure ready</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Unlimited transactions</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">Premium support included</span>
              </div>
              <div className="flex items-center space-x-3">
                <CheckCircle className="w-5 h-5 text-green-500" />
                <span className="text-slate-700">30-day money-back guarantee</span>
              </div>
            </div>

            <button className="w-full px-8 py-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-all hover:shadow-lg">
              Add to Cart
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}
